import { extractJsonFromMarkdown } from './index';

/**
 * Lê o stream SSE e retorna a mensagem final do evento "done".
 * @param stream AsyncIterable do stream SSE
 * @returns Mensagem final normalizada
 */
export const readStreamFinalMessage = async (
  stream: AsyncIterable<string>
): Promise<string> => {
  let buffer = '';
  let pendingEvent: string | null = null;
  let lastStreamErrorMessage: string | null = null;

  const normalizeLine = (line: string) =>
    line.startsWith('data:') ? line.replace(/^data:\s*/, '') : line;

  for await (const chunk of stream) {
    buffer += chunk;
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('event:')) {
        pendingEvent = trimmed.replace(/^event:\s*/, '');
        continue;
      }

      if (!trimmed.startsWith('data:')) {
        continue;
      }

      const payloadText = normalizeLine(trimmed);
      if (!payloadText) {
        continue;
      }

      let parsedPayload: any = null;
      try {
        parsedPayload = JSON.parse(payloadText);
      } catch {
        parsedPayload = null;
      }

      if (pendingEvent === 'message' && parsedPayload?.type === 'error') {
        const message = parsedPayload?.message || 'Erro durante o stream';
        throw new Error(String(message));
      }

      if (pendingEvent === 'done') {
        const parsed = parsedPayload ?? JSON.parse(payloadText);
        if (parsed?.success === false) {
          throw new Error(parsed?.message || 'Execução retornou erro');
        }
        if (!parsed?.message) {
          throw new Error('Resposta final sem message');
        }
        return String(parsed.message).trim();
      }

      if (
        pendingEvent === 'message' &&
        parsedPayload?.type === 'status' &&
        parsedPayload?.stage === 'error' &&
        parsedPayload?.message
      ) {
        lastStreamErrorMessage = String(parsedPayload.message);
      }
    }
  }

  if (buffer.trim()) {
    const trimmed = buffer.trim();
    if (trimmed.startsWith('data:') && pendingEvent === 'done') {
      const payloadText = normalizeLine(trimmed);
      const parsed = JSON.parse(payloadText);
      if (parsed?.success === false) {
        throw new Error(parsed?.message || 'Execução retornou erro');
      }
      if (!parsed?.message) {
        throw new Error('Resposta final sem message');
      }
      return String(parsed.message).trim();
    }
  }

  if (lastStreamErrorMessage) {
    throw new Error(lastStreamErrorMessage);
  }

  throw new Error('Evento done não encontrado no stream');
};

/**
 * Faz parse resiliente do resultado retornado pela IA.
 * @param rawResponse Resposta bruta do stream
 * @returns Objeto/valor parseado
 */
export const parseStreamResult = (rawResponse: string): any => {
  if (!rawResponse) {
    throw new Error('Resposta vazia da crew');
  }

  const extractJsonCandidate = (value: string) => {
    try {
      return extractJsonFromMarkdown(value);
    } catch {
      return null;
    }
  };

  const parsedResponse = (() => {
    const extracted = extractJsonCandidate(rawResponse);
    const candidate = extracted ? JSON.stringify(extracted) : rawResponse;
    try {
      return JSON.parse(candidate);
    } catch {
      return extracted ?? rawResponse;
    }
  })();

  if (parsedResponse?.success === false) {
    throw new Error(parsedResponse.message || 'Resposta inválida da crew');
  }

  const resultPayload = parsedResponse?.result ?? parsedResponse;
  if (typeof resultPayload === 'string') {
    const extractedResult = extractJsonCandidate(resultPayload);
    if (extractedResult) {
      return extractedResult;
    }

    try {
      return JSON.parse(resultPayload);
    } catch {
      return resultPayload;
    }
  }

  return resultPayload;
};
